import React, { useEffect, useMemo, useRef } from 'react';
import { Chart, ChartData, ChartOptions } from 'chart.js';
import styled from '@emotion/styled';

type Props = {
  data: ChartData<'doughnut'>;
  options?: ChartOptions<'doughnut'>;
  width?: string;
  height?: string;
};

// Module-level so the default doesn't hand the effect below a new object on
// every render of a caller that passes no options.
const NO_OPTIONS: ChartOptions<'doughnut'> = {};

// Chart.js tracks animation state on the dataset objects it already holds, so
// assigning `chart.data` wholesale hands it unfamiliar objects and it grows the
// arcs back in from zero. Copying the incoming values into the datasets already
// on the chart lets it tween from what is currently on screen instead.
const applyData = (
  chart: Chart<'doughnut'>,
  next: ChartData<'doughnut'>,
): void => {
  const current = chart.data;
  if (current.datasets.length !== next.datasets.length) {
    chart.data = next;
    return;
  }
  current.labels = next.labels;
  next.datasets.forEach((dataset, index) =>
    Object.assign(current.datasets[index], dataset),
  );
};

const Doughnut: React.FC<React.PropsWithChildren<Props>> = ({
  data,
  options = NO_OPTIONS,
  children,
  width,
  height,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart<'doughnut'>>();
  const drawn = useRef<string>();

  // Callers build `data` (and often `options`) inline from props, so both are
  // fresh objects on every render even when nothing about the chart changed.
  // Compare them by value so an unrelated re-render never reaches the canvas.
  // Functions drop out of the signature, so a caller that rebuilds a tooltip
  // formatter each render doesn't count as a change — only the drawn values do.
  const signature = useMemo(
    () => JSON.stringify({ data, options }),
    [data, options],
  );

  // Create the Chart.js instance once, then push only genuine value changes
  // into it. Rebuilding (or handing it fresh dataset objects) replays the entry
  // animation from zero, which is what makes an ordinary re-render look like
  // the chart is redrawing itself.
  useEffect(() => {
    const instance = chart.current;

    if (!instance) {
      const context = canvasRef.current?.getContext('2d');
      if (context) {
        chart.current = new Chart<'doughnut'>(context, {
          type: 'doughnut',
          data,
          options,
        });
        drawn.current = signature;
      }
      return;
    }

    // Already showing these numbers — leave the canvas alone.
    if (drawn.current === signature) {
      return;
    }
    drawn.current = signature;

    applyData(instance, data);
    instance.options = options;
    instance.update();
  }, [signature, data, options]);

  // Tear down only on unmount — never between renders.
  useEffect(
    () => () => {
      chart.current?.destroy();
      chart.current = undefined;
    },
    [],
  );

  return (
    <StyledChart width={width} height={height} className="chart">
      <canvas className="chart-canvas" ref={canvasRef} />
      <InsideText>{children}</InsideText>
    </StyledChart>
  );
};

const StyledChart = styled.div<{ width?: string; height?: string }>(
  {
    position: 'relative',
    height: '200px',
  },
  ({ width }) =>
    width && {
      width: width,
    },
  ({ height }) =>
    height && {
      height: height,
    },
);

const InsideText = styled.span`
  position: absolute;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export default Doughnut;
